import { useState, useEffect } from "react";
import { useAppData } from "../../lib/AppDataContext";
import { useAuth } from "../../lib/AuthContext";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { toast } from "../../components/shared/Toast";

export function SiteContactSettings() {
  const { siteSettings, siteSettingsLoaded, updateSiteSettings } = useAppData();
  const { user } = useAuth();
  const role = user?.role || "employee";

  const [form, setForm] = useState(siteSettings || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Hydrate form once settings load
  useEffect(() => {
    if (siteSettings) setForm(siteSettings);
  }, [siteSettings]);

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  async function handleSave() {
    setSaving(true);
    try {
      await updateSiteSettings(form);
      setDirty(false);
    } catch {
      // upstream toasted
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    if (!confirm("Discard unsaved changes?")) return;
    setForm(siteSettings || {});
    setDirty(false);
  }

  const readOnly = role === "employee";

  if (!siteSettingsLoaded) {
    return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  }

  return (
    <div>
      <PageHeader accent="content"
        title="Site Contact Settings"
        subtitle="These details power the footer, WhatsApp buttons, and default advisor card on the customer site."
        action={
          dirty && (
            <div className="flex gap-2">
              <button onClick={handleReset} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">Discard</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )
        }
      />

      <div className="space-y-4 max-w-4xl">
        <Section title="Phone & WhatsApp">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Primary phone">
              <input value={form.primaryPhone ?? ""} onChange={(e) => update({ primaryPhone: e.target.value })} disabled={readOnly} className="form-input" placeholder="9730302843" />
            </Field>
            <Field label="Secondary phone">
              <input value={form.secondaryPhone ?? ""} onChange={(e) => update({ secondaryPhone: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="WhatsApp (footer button)">
              <input value={form.whatsappFooter ?? ""} onChange={(e) => update({ whatsappFooter: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="WhatsApp (floating button)">
              <input value={form.whatsappFloating ?? ""} onChange={(e) => update({ whatsappFloating: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
          </div>
        </Section>

        <Section title="Advisor Card Contact (shown after OTP verification)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Property Advisor Contact">
              <input 
                value={form.contactPhoneProperty ?? ""} 
                onChange={(e) => update({ contactPhoneProperty: e.target.value })} 
                disabled={readOnly} 
                className="form-input" 
                placeholder="+91 98765 43210" 
              />
            </Field>
            <Field label="Interior Advisor Contact">
              <input 
                value={form.contactPhoneInterior ?? ""} 
                onChange={(e) => update({ contactPhoneInterior: e.target.value })} 
                disabled={readOnly} 
                className="form-input" 
                placeholder="+91 98765 43211" 
              />
            </Field>
          </div>
        </Section>

        <Section title="Email & address">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Public email">
              <input value={form.email ?? ""} onChange={(e) => update({ email: e.target.value })} disabled={readOnly} className="form-input" placeholder="contact@abhighar.com" />
            </Field>
            <Field label="MAHARERA Registration ID">
              <input value={form.reraId ?? ""} onChange={(e) => update({ reraId: e.target.value })} disabled={readOnly} className="form-input" placeholder="A031262401068" />
            </Field>
            <Field label="Hours displayed">
              <input value={form.hours ?? ""} onChange={(e) => update({ hours: e.target.value })} disabled={readOnly} className="form-input" placeholder="Mon – Sun · 10 AM – 8 PM" />
            </Field>
            <Field label="Address" colSpan="sm:col-span-2">
              <textarea value={form.address ?? ""} onChange={(e) => update({ address: e.target.value })} disabled={readOnly} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Google Maps embed URL (iframe src)" colSpan="sm:col-span-2">
              <input value={form.mapEmbedUrl ?? ""} onChange={(e) => update({ mapEmbedUrl: e.target.value })} disabled={readOnly} className="form-input" placeholder="https://www.google.com/maps/embed?…" />
            </Field>
          </div>
        </Section>

        <Section title="Social links">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Instagram">
              <input value={form.instagramUrl ?? ""} onChange={(e) => update({ instagramUrl: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="Facebook">
              <input value={form.facebookUrl ?? ""} onChange={(e) => update({ facebookUrl: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="YouTube">
              <input value={form.youtubeUrl ?? ""} onChange={(e) => update({ youtubeUrl: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="LinkedIn">
              <input value={form.linkedinUrl ?? ""} onChange={(e) => update({ linkedinUrl: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="X (Twitter)">
              <input value={form.twitterUrl ?? ""} onChange={(e) => update({ twitterUrl: e.target.value })} disabled={readOnly} className="form-input" placeholder="https://x.com/abhighar" />
            </Field>
          </div>
        </Section>

        <Section title="Default advisor (fallback for properties without one)">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label="Name">
              <input value={form.defaultAdvisorName ?? ""} onChange={(e) => update({ defaultAdvisorName: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="Initials (2-4)">
              <input value={form.defaultAdvisorInitials ?? ""} maxLength={4} onChange={(e) => update({ defaultAdvisorInitials: e.target.value.toUpperCase() })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="Role">
              <input value={form.defaultAdvisorRole ?? ""} onChange={(e) => update({ defaultAdvisorRole: e.target.value })} disabled={readOnly} className="form-input" placeholder="Senior Advisor" />
            </Field>
            <Field label="Rating (0-5)">
              <input type="number" step="0.1" min="0" max="5" value={form.defaultAdvisorRating ?? ""} onChange={(e) => update({ defaultAdvisorRating: e.target.value === "" ? null : Number(e.target.value) })} disabled={readOnly} className="form-input" />
            </Field>
          </div>
        </Section>

        <Section title="Branding">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Site name">
              <input value={form.siteName ?? ""} onChange={(e) => update({ siteName: e.target.value })} disabled={readOnly} className="form-input" placeholder="Abhi Ghar" />
            </Field>
            <Field label="MAHARERA registration ID">
              <input value={form.reraId ?? ""} onChange={(e) => update({ reraId: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
            <Field label="Tagline" colSpan="sm:col-span-2">
              <input value={form.tagline ?? ""} onChange={(e) => update({ tagline: e.target.value })} disabled={readOnly} className="form-input" />
            </Field>
          </div>
        </Section>

        {dirty && (
          <div className="sticky bottom-2 lg:static bg-amber-50 border border-amber-200 rounded p-3 flex justify-between items-center flex-wrap gap-2">
            <p className="text-sm text-amber-800">You have unsaved changes.</p>
            <div className="flex gap-2">
              <button onClick={handleReset} className="px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-100 rounded">Discard</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid rgb(203 213 225);
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .form-input:disabled {
          background: rgb(248 250 252);
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card className="p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      {children}
    </Card>
  );
}

function Field({ label, children, colSpan }) {
  return (
    <div className={colSpan || ""}>
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
